import getVWKey from "../helpers/getVWKey"
import Utils from "../utils";
import * as Events from "../utils/events";
import AssetLibrary from "../assets/asset-library";
import Base2DElement from "./base-2d-element";

class EmFieldUI extends Base2DElement{
    constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
        super(options, parentLayer, uuid)

        this._initControls();

        this._needsUpdate = {
            visible: true,
            controlsVisible: true,
            values: true,
        };

        this._eventsUpdate = [
            { event: 'em_field_toggle_visibility', updateProperty: 'visible' },
            { event: 'em_field_toggle_controls', updateProperty: 'controlsVisible' },
            { event: 'em_field_change_param', updateProperty: 'values' },
        ];

        for (let eventInfo of this._eventsUpdate) {
            eventInfo.updateFunction = (field) => {
                if (field.uuid === this.field) {
                    this._needsUpdate[eventInfo.updateProperty] = true;
                }
            };

            Events.listen(eventInfo.event, eventInfo.updateFunction);
        }
    }

    _initValues(options) {
        this.type = "2DObject";
        this.type2d = "EmFieldUI";
        this.index = options.index;
        this.field = options.field;
    }

    _createContainer() {
        const container = super._createContainer();
        
        container.classList.add("coil-em-field-controls");

        return container;
    }

    _getFieldData() {
        return Q3.data.assets.objects[this.field];
    }

    _getFieldObject() {
        return AssetLibrary.objects[this.field];
    }

    setField(field) {
        this.field = field;

        for (let property in this._needsUpdate) {
            this._needsUpdate[property] = true;
        }
    }

    redraw(width, height) {
        let update = this._needsUpdate;

        if (update.visible || update.controlsVisible) {
            this._updateControlVisibility();
            update.visible = false;
            update.controlsVisible = false;
        }

        if (update.values) {
            this._updateControlLabels();
            this._updateControlSliders();
            update.values = false;
        }
    }

    render(width, height) {
        if (!Q3.data.layers.layers2D[this.parentLayer.uuid] || this.disabled || Utils.queryParams.has("disable_2d")) return;

        if (!this.isMounted) {
            for (let property in this._needsUpdate) {
                this._needsUpdate[property] = true;
            }

            if (width > 0 && height > 0) {
                this.isMounted = true;
            }
        }

        this.redraw(width, height);
    }

    delete() {

        for (let eventInfo of this._eventsUpdate) {
            Events.listen(eventInfo.event, eventInfo.updateFunction);
        }

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        // this.parentLayer.domElement.removeChild(this.domElement);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }

    _initControls() {
        let params = [
            { property: 'turns', name: "Coil Turns", units: '', range: [1, 100], step: 1, precision: 0 },
            { property: 'current', name: "Current", units: 'A', range: [0.01, 100], step: 0.01, precision: 2 },
            { property: 'voltage', name: "Voltage", units: 'V', range: [0.01, 100], step: 0.01, precision: 2 },
        ];

        for (let param of params) {
            let paramCnt = document.createElement("div");
            let label = document.createElement("div");
            let slider = document.createElement("input");

            param.dom = {
                param: paramCnt,
                label: label,
                slider: slider,
            };
            paramCnt.classList.add("param");

            slider.setAttribute("type", "range");
            slider.setAttribute("min", param.range[0]);
            slider.setAttribute("max", param.range[1]);
            slider.setAttribute("step", param.step);

            let camControlsEnabled;
            slider.addEventListener("mouseover", () => {
                camControlsEnabled = Q3.controls.enabled;
                Q3.controls.enabled = false;
            });
            slider.addEventListener("mouseout", () => {
                if (camControlsEnabled !== undefined) {
                    Q3.controls.enabled = camControlsEnabled;
                    camControlsEnabled = undefined;
                }
            });
            slider.addEventListener("mousemove", () => {
                if (camControlsEnabled !== undefined) {
                    Q3.controls.enabled = false;
                }
            });
            slider.addEventListener("mouseup", () => {
                if (camControlsEnabled !== undefined) {
                    Q3.controls.enabled = false;
                }
            });
            slider.addEventListener("mousedown", () => {
                if (camControlsEnabled !== undefined) {
                    Q3.controls.enabled = false;
                }
            });

            let prop = param.property;
            slider.addEventListener("input", (e) => {
                let field = this._getFieldObject();
                if (!field) return;

                let value = Number(slider.value);
                this._controls.updatingValueFromSlider = true;
                field[prop] = value;
                this._controls.updatingValueFromSlider = false;

                Events.fire("em_field_ui_change_value", this.field, prop, value);
            });

            slider.addEventListener("change", function () {
                Events.fire("em_field_ui_updated");
            });

            this.domElement.appendChild(paramCnt);
            paramCnt.appendChild(label);
            paramCnt.appendChild(slider);
        }

        let updatingValueFromSlider = false;
        this._controls = {
            container: this.domElement,
            params,
            updatingValueFromSlider,
        };
    }

    _updateControlVisibility() {
        let field = this._getFieldObject();

        let show = field && field.visible && field.controlsEnabled;
        let hide = !show;
        this.domElement.classList.toggle("hidden", hide);

        Events.fire("em_field_ui_updated");
    }

    _updateControlLabels() {
        let field = this._getFieldObject();
        if (!field) return;

        for (let param of this._controls.params) {
            let label = param.dom.label;
            let value = field[param.property];

            label.innerText = param.name + ': ' + value.toFixed(param.precision) + param.units;
        }

        Events.fire("em_field_ui_updated");
    }

    _updateControlSliders() {
        if (this._controls.updatingValueFromSlider) return;

        let field = this._getFieldObject();
        if (!field) return;

        for (let param of this._controls.params) {
            let slider = param.dom.slider;
            let value = field[param.property];

            slider.value = value;
        }

        Events.fire("em_field_ui_updated");
    }
}

export default EmFieldUI;
export { EmFieldUI };
